'use client';

import React, { useState, useEffect } from 'react';
import { Building2 } from 'lucide-react';
import { getBankColor, getBankFallbackLogo } from '@/utils/bankUtils';

export interface BankIconProps {
  bankValue?: string | null; // e.g. "SCB", "KBANK", "BBL"
  logoImage?: string | null; // primary image from API/backend
  bank?: {
    bankCode?: string | null;
    bankPrefix?: string | null;
    bankNameTH?: string | null;
    bankNameEN?: string | null;
  } | null;
  size?: 'small' | 'mobile' | 'default' | 'desktop' | 'large' | 'xl' | 'xxl' | 'custom';
  className?: string;
  isActive?: boolean;
  isCorporate?: boolean;
}

const sizeClasses = {
  small: 'w-7 h-7 text-xs rounded-lg p-0.5',
  mobile: 'w-8 h-8 text-xs rounded-lg p-1',
  default: 'w-9 h-9 text-sm rounded-xl p-1',
  desktop: 'w-10 h-10 text-sm rounded-xl p-1',
  large: 'w-12 h-12 text-base rounded-xl p-1',
  xl: 'w-16 h-16 text-lg rounded-2xl p-1',
  xxl: 'w-20 h-20 text-xl rounded-2xl p-2',
  custom: '',
};

export function BankIcon({
  bankValue,
  logoImage,
  bank,
  size = 'default',
  className = '',
  isActive = true,
  isCorporate = false,
}: BankIconProps) {
  const [imageError, setImageError] = useState(false);
  const [fallbackError, setFallbackError] = useState(false);

  // Sync state if logoImage changes
  useEffect(() => {
    setImageError(false);
    setFallbackError(false);
  }, [logoImage, bankValue, bank]);

  const opacityClass = isActive ? '' : 'opacity-50 grayscale';
  const resolvedBankKey = (bankValue || bank?.bankPrefix || bank?.bankCode || '').toUpperCase().trim();

  // Try to resolve the color for background fallback
  const resolvedBankName = bank?.bankNameEN || bankValue || '';
  const bgColorHex = getBankColor(resolvedBankName || resolvedBankKey);

  // Determine fallback text (e.g. SCB, BBL, KBANK)
  const fallbackText = resolvedBankKey || resolvedBankName.slice(0, 4).toUpperCase();

  const renderAvatar = () => {
    // 1. Try backend/API logo first
    if (logoImage && !imageError) {
      return (
        <div
          className={`flex items-center justify-center flex-shrink-0 transition-all duration-200 overflow-hidden ${
            size !== 'custom' ? sizeClasses[size] : ''
          } ${opacityClass} ${className}`}
        >
          <img
            src={logoImage}
            alt={resolvedBankName || 'Bank Logo'}
            className="w-full h-full object-contain rounded-lg"
            onError={() => setImageError(true)}
            loading="lazy"
          />
        </div>
      );
    }

    // 2. Try local fallback logo from utility or local assets (/images/bankLogo/...)
    const fallbackObj = bank || {
      bankCode: bankValue,
      bankPrefix: bankValue,
      bankNameEN: bankValue,
      bankNameTH: bankValue,
    };
    const localLogo = getBankFallbackLogo(fallbackObj);

    if (localLogo && !fallbackError) {
      return (
        <div
          className={`flex items-center justify-center flex-shrink-0 transition-all duration-200 overflow-hidden ${
            size !== 'custom' ? sizeClasses[size] : ''
          } ${opacityClass} ${className}`}
        >
          <img
            src={localLogo}
            alt={resolvedBankName || 'Bank Logo'}
            className="w-full h-full object-contain rounded-lg"
            onError={() => setFallbackError(true)}
            loading="lazy"
          />
        </div>
      );
    }

    // 3. Fallback to colored text box using bank color from bankUtils or gray
    const hasValidColor = bgColorHex && bgColorHex !== '#64748b';

    return (
      <div
        className={`flex items-center justify-center flex-shrink-0 text-white font-bold transition-all duration-200 overflow-hidden ${
          size !== 'custom' ? sizeClasses[size] : ''
        } ${opacityClass} ${className}`}
        style={{
          backgroundColor: hasValidColor ? bgColorHex : '#4b5563',
        }}
      >
        {fallbackText ? (
          <span className="truncate text-[10px] font-black tracking-wider uppercase">{fallbackText}</span>
        ) : (
          <Building2 className="h-1/2 w-1/2 text-white" />
        )}
      </div>
    );
  };

  if (isCorporate) {
    return (
      <div className="relative inline-flex shrink-0">
        {renderAvatar()}
        <div className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 sm:w-4 sm:h-4 rounded-full bg-gradient-to-tr from-amber-700 to-amber-500 border border-slate-900 flex items-center justify-center shadow-xs z-10">
          <Building2 className="w-2 h-2 sm:w-2.5 sm:h-2.5 text-amber-100" />
        </div>
      </div>
    );
  }

  return renderAvatar();
}

export default BankIcon;
